// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import FullscreenOverlay from '@ta/modal.fullscreen';
import { CloseIcon as CloseX } from '@ta/modal.core';
import { Closer, Global, Opener } from '@ta/overlays.managers';
import { Transition, Target } from '@ta/overlays.pieces';
import TargetedFlyoutContents from './TargetedFlyoutContents';
/* eslint css-modules/no-unused-class: [2, { markAsUsed: ['container']}] */
import styles from './geoPillCommon.less';

type ExternalProps = {|
  /** Contents of the overlay */
  overlayContents: React.Node,

  /** Trigger contents. targetRef should be set on the intended trigger as a ref. */
  children: (setTarget: () => void) => React.Node,

  /** Pixel value of the top offset of the overlay from the top of viewport. */
  offsetTop: number,

  /** Invoked when the overlay is opened */
  onOpen?: () => void,

  /** Invoked when the overlay is closed */
  onClose?: () => void,
|};

type Props = {|
  ...ExternalProps,

  /** Whether to show the flyout in fullscreen (used on mobile) */
  fullscreen: boolean,

  /** Whether we want to show the fly-from-target animation. For slower devices like tablet we set to false to avoid choppiness. */
  animate: boolean,
|};

type State = {|
  /** Name of the overlay */
  name: string,

  /** The scroll-Y offset at which we opened the overlay */
  openedAt: number,
|};

const nextName: () => string = (() => {
  let i = 0;
  return () => {
    i += 1;
    return `targeted_flyout_${i}`;
  };
})();

export const TRANSITION_TIMING = 150;

/**
 * An overlay with an animation that shows it popping out from a target. Starts from the center of the target and scales to
 * the actual size of its contents. Not in @ta/overlays.* because it requires some more design discussion.
 */
class TargetedFlyout extends React.Component<Props, State> {
  static defaultProps = {
    animate: true,
  };

  state = {
    name: nextName(),
    openedAt: 0,
  };

  overlayContent = (close: () => void) => (
    <div className={styles.overlayContents}>
      {this.props.overlayContents}
      <CloseX onClick={close} />
    </div>
  );

  render() {
    const transitionTiming = this.props.animate ? TRANSITION_TIMING : 0;
    const onOpen = this.props.onOpen || (() => {});
    const onClose = this.props.onClose || (() => {});

    return (
      <Opener name={this.state.name}>
        {(open, isOpen) => {
          const opener = () => {
            // eslint-disable-next-line no-restricted-globals
            this.setState({ openedAt: window.pageYOffset });
            open();
            onOpen();
          };
          return (
            <Closer>
              {close => {
                const closer = () => {
                  close();
                  onClose();
                };

                return (
                  <Target>
                    {(setTarget, target) => (
                      <React.Fragment>
                        <div onClick={isOpen ? closer : opener}>
                          {this.props.children(setTarget)}
                        </div>

                        {this.props.fullscreen ? (
                          <Global name={this.state.name}>
                            <React.Fragment>
                              <FullscreenOverlay>{this.overlayContent(closer)}</FullscreenOverlay>
                            </React.Fragment>
                          </Global>
                        ) : (
                          <Global name={this.state.name} transitionDuration={transitionTiming}>
                            <Transition.Consumer>
                              {transitionStatus => (
                                <TargetedFlyoutContents
                                  offsetTop={this.props.offsetTop}
                                  transitionStatus={transitionStatus}
                                  close={closer}
                                  target={target}
                                  overlayContents={this.overlayContent(closer)}
                                  openedAt={this.state.openedAt}
                                  transitionTiming={transitionTiming}
                                />
                              )}
                            </Transition.Consumer>
                          </Global>
                        )}
                      </React.Fragment>
                    )}
                  </Target>
                );
              }}
            </Closer>
          );
        }}
      </Opener>
    );
  }
}

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  fullscreen: state.meta.device.viewportCategory === 'MOBILE',
  // Unfortunately we need to check userAgentCategory because iPad landscape can reach 1024px width, the beginning of the desktop range,
  // and we don't want slower devices like iPads to attempt the animation (it's significantly choppy).
  animate: state.meta.device.userAgentCategory === 'DESKTOP',
}))(TargetedFlyout);
